/**
 * Super-admin API client. Impersonation swaps AUTH_TOKEN for the target user while stashing the
 * admin token in IMPERSONATION_ADMIN_TOKEN; exitImpersonation restores the admin session.
 */
import { API_URL } from '../../../constant/API_Settings';
import { savePermissions } from '../../../utils/permissions';
import { persistSubscriptionAccess } from '../../../utils/subscriptionAccess';

function authHeaders() {
  return {
    'Content-Type': 'application/json',
    Authorization: 'Bearer ' + localStorage.getItem('AUTH_TOKEN'),
    Accept: 'application/json',
  };
}

async function parseJson(response) {
  let data = {};
  try {
    data = await response.json();
  } catch {
    // Non-JSON body (timeout / fatal server error)
  }
  if (!response.ok) {
    if (response.status >= 500) {
      throw new Error(
        data.message
          || 'Server timed out or failed — large pumps can take 1–2 minutes. Try a shorter date range or wait and check Fix history before retrying.'
      );
    }
    const validationError = data.errors
      ? Object.values(data.errors).flat().find(Boolean)
      : null;
    throw new Error(validationError || data.message || `Request failed (${response.status})`);
  }
  return data;
}

export function friendlyFetchError(error) {
  const message = error?.message || '';
  if (message.includes('Failed to fetch') || message.includes('NetworkError') || message.includes('Load failed')) {
    return 'Could not reach the server — the check may have timed out. Wait a minute, then try a shorter date range or click Check again.';
  }
  return message || 'Something went wrong. Please try again.';
}

export function fetchPumpNames() {
  return fetch(`${API_URL}/get-pump-name`, {
    method: 'POST',
    headers: authHeaders(),
  }).then(parseJson);
}

export function fetchPumpLockDates(pumpId) {
  return fetch(`${API_URL}/get-pump-lock-dates`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ pump_id: pumpId }),
  }).then(parseJson);
}

export function savePumpLockDates(pumpId, startDate, endDate) {
  return fetch(`${API_URL}/save-pump-lock-dates`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      pump_id: pumpId,
      ac_loc_sys_start_date: startDate || '',
      ac_loc_sys_end_date: endDate || '',
    }),
  }).then(parseJson);
}

export function fetchAdminOverview() {
  return fetch(`${API_URL}/admin/overview`, { headers: authHeaders() }).then(parseJson);
}

export function fetchAdminPumps(search = '', page = 1) {
  const params = new URLSearchParams({ page, search_keyword: search });
  return fetch(`${API_URL}/admin/pumps?${params}`, { headers: authHeaders() }).then(parseJson);
}

export function fetchAdminPumpDetail(pumpId) {
  return fetch(`${API_URL}/admin/pump/${pumpId}`, { headers: authHeaders() }).then(parseJson);
}

export function fetchAdminPumpAnalytics(pumpId, fromDate, toDate) {
  const params = new URLSearchParams();
  if (fromDate) params.set('from_date', fromDate);
  if (toDate) params.set('to_date', toDate);
  const qs = params.toString() ? `?${params}` : '';
  return fetch(`${API_URL}/admin/pump/${pumpId}/analytics${qs}`, { headers: authHeaders() }).then(parseJson);
}

export function fetchAdminPumpAnalyticsDateDefaults(pumpId) {
  return fetch(`${API_URL}/admin/pump/${pumpId}/analytics/date-defaults`, { headers: authHeaders() }).then(parseJson);
}

export function fetchAdminPumpOwnerDashboard(pumpId, endpoint, fromDate, toDate, limit = 10) {
  return fetch(`${API_URL}/admin/pump/${pumpId}/owner-dashboard/${endpoint}`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ from_date: fromDate, to_date: toDate, limit }),
  }).then(parseJson);
}

export function fetchAdminAlerts(pumpId = null) {
  const params = pumpId ? `?pump_id=${pumpId}` : '';
  return fetch(`${API_URL}/admin/alerts${params}`, { headers: authHeaders() }).then(parseJson);
}

export function fetchLedgerMismatches({ onlyErrors = true, refresh = false } = {}) {
  const params = new URLSearchParams();
  params.set('only_errors', onlyErrors ? '1' : '0');
  if (refresh) params.set('refresh', '1');
  return fetch(`${API_URL}/admin/ledger-mismatches?${params}`, { headers: authHeaders() }).then(parseJson);
}

export function fetchLedgerMismatchDetail(pumpId) {
  return fetch(`${API_URL}/admin/ledger-mismatches/${pumpId}`, { headers: authHeaders() }).then(parseJson);
}

export function fetchTankStockMismatches({
  onlyErrors = true,
  refresh = false,
  fromDate = '',
  toDate = '',
  status = 'open',
} = {}) {
  const params = new URLSearchParams();
  params.set('only_errors', onlyErrors ? '1' : '0');
  params.set('status', status || 'open');
  if (refresh) params.set('refresh', '1');
  if (fromDate) params.set('from_date', fromDate);
  if (toDate) params.set('to_date', toDate);
  return fetch(`${API_URL}/admin/tank-stock-mismatches?${params}`, { headers: authHeaders() }).then(parseJson);
}

export function fetchTankStockMismatchDetail(pumpId, { fromDate = '', toDate = '' } = {}) {
  const params = new URLSearchParams();
  if (fromDate) params.set('from_date', fromDate);
  if (toDate) params.set('to_date', toDate);
  const qs = params.toString();
  return fetch(`${API_URL}/admin/tank-stock-mismatches/${pumpId}${qs ? `?${qs}` : ''}`, {
    headers: authHeaders(),
  }).then(parseJson);
}

export function fixTankStockMismatch(pumpId, { tankId, opDate, reason, previewOnly = false }) {
  return fetch(`${API_URL}/admin/tank-stock-mismatches/${pumpId}/fix`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      tank_id: tankId,
      op_date: opDate,
      reason,
      preview_only: previewOnly ? 1 : 0,
    }),
  }).then(parseJson);
}

export function fetchAdminPlans() {
  return fetch(`${API_URL}/admin/plans`, { headers: authHeaders() }).then(parseJson);
}

export function adminAddSubscription(pumpId, payload) {
  return fetch(`${API_URL}/admin/pump/${pumpId}/subscription`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  }).then(parseJson);
}

export function adminActivatePumpAccess(pumpId, waived = true) {
  return fetch(`${API_URL}/admin/pump/${pumpId}/activate-access`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ waived }),
  }).then(parseJson);
}

export function adminRenewSubscription(subscriptionId, payload = {}) {
  return fetch(`${API_URL}/admin/subscription/${subscriptionId}/renew`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  }).then(parseJson);
}

export function adminMarkSubscriptionPaid(subscriptionId, payload = {}) {
  return fetch(`${API_URL}/admin/subscription/${subscriptionId}/mark-paid`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  }).then(parseJson);
}

export function adminRecordSubscriptionPayment(subscriptionId, payload) {
  return fetch(`${API_URL}/admin/subscription/${subscriptionId}/payment`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  }).then(parseJson);
}

export function fetchAdminSubscriptions({ search = '', filter = '', page = 1, perPage = 100 } = {}) {
  const params = new URLSearchParams({ page: String(page), per_page: String(perPage) });
  if (search) params.set('search', search);
  if (filter) params.set('filter', filter);
  return fetch(`${API_URL}/admin/subscriptions?${params}`, { headers: authHeaders() }).then(parseJson);
}

export function adminDeleteSubscription(subscriptionId) {
  return fetch(`${API_URL}/admin/subscription/${subscriptionId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  }).then(parseJson);
}

export function fetchPumpSubscriptionControl(pumpId) {
  return fetch(`${API_URL}/admin/pump/${pumpId}/subscription-control`, {
    headers: authHeaders(),
  }).then(parseJson);
}

export function adminUpdateSubscription(subscriptionId, payload) {
  return fetch(`${API_URL}/admin/subscription/${subscriptionId}`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  }).then(parseJson);
}

export function adminAdjustPumpInvoice(pumpId, invoiceId, payload) {
  return fetch(`${API_URL}/admin/pump/${pumpId}/invoices/${invoiceId}/adjust`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  }).then(parseJson);
}

export function updateInvoiceReceipt(pumpId, invoiceId, receiptId, payload) {
  return fetch(`${API_URL}/admin/pump/${pumpId}/invoices/${invoiceId}/receipts/${receiptId}`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  }).then(parseJson);
}

export function fetchAdminPayments({ search = '', status = '', gateway = '', page = 1, perPage = 50 } = {}) {
  const params = new URLSearchParams({ page: String(page), per_page: String(perPage) });
  if (search) params.set('search', search);
  if (status !== '') params.set('status', status);
  if (gateway) params.set('gateway', gateway);
  return fetch(`${API_URL}/admin/payments?${params}`, { headers: authHeaders() }).then(parseJson);
}

export function fetchAdminPaymentDetail(paymentId) {
  return fetch(`${API_URL}/admin/payments/${paymentId}`, { headers: authHeaders() }).then(parseJson);
}

export function adminCheckPaymentStatus(paymentId) {
  return fetch(`${API_URL}/admin/payments/${paymentId}/check-status`, {
    method: 'POST',
    headers: authHeaders(),
  }).then(parseJson);
}

export function adminResetPassword(userId, password) {
  return fetch(`${API_URL}/admin/user/${userId}/reset-password`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ password }),
  }).then(parseJson);
}

export function adminSetUserLock(userId, locked) {
  return fetch(`${API_URL}/admin/user/${userId}/lock`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ locked }),
  }).then(parseJson);
}

export function adminImpersonate(userId) {
  return fetch(`${API_URL}/admin/impersonate`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ user_id: userId }),
  }).then(parseJson);
}

/** Restore super-admin AUTH_TOKEN from IMPERSONATION_ADMIN_TOKEN; notify backend then reload admin dashboard. */
export function exitImpersonation() {
  const adminToken = localStorage.getItem('IMPERSONATION_ADMIN_TOKEN');
  if (!adminToken) return;

  const restoreAdminSession = () => {
    localStorage.setItem('AUTH_TOKEN', adminToken);
    localStorage.removeItem('IMPERSONATION_ADMIN_TOKEN');
    localStorage.removeItem('IMPERSONATION_ADMIN_NAME');
    window.location.href = '/admin_dashboard';
  };

  fetch(`${API_URL}/exit-impersonation`, {
    method: 'POST',
    headers: authHeaders(),
  })
    .catch(() => null)
    .finally(restoreAdminSession);
}

/** Enter impersonation: stash admin token/name, replace AUTH_TOKEN + permissions with target user. */
export function applyImpersonation(payload, originalAdminName) {
  localStorage.setItem('IMPERSONATION_ADMIN_TOKEN', localStorage.getItem('AUTH_TOKEN'));
  localStorage.setItem('IMPERSONATION_ADMIN_NAME', originalAdminName || 'Super Admin');
  localStorage.setItem('AUTH_TOKEN', payload.token);
  localStorage.setItem('PROFILE_NAME', payload.user_name || '');
  savePermissions(payload.permissions || []);
  if (payload.role?.id) {
    localStorage.setItem('ROLE_ID', String(payload.role.id));
  }
  if (payload.pump_id) {
    localStorage.setItem('admin_pump_id', payload.pump_id);
    localStorage.setItem('PUMP_ID', payload.pump_id);
  }
  persistSubscriptionAccess(payload);
}

export function isImpersonating() {
  return !!localStorage.getItem('IMPERSONATION_ADMIN_TOKEN');
}

export function fetchAdminCharts() {
  return fetch(`${API_URL}/admin/charts`, { headers: authHeaders() }).then(parseJson);
}

/** Platform pump usage + suggested license slabs. scope: operational|active_status|all */
export function fetchAdminPumpUsageAnalytics(scope = 'operational') {
  const params = new URLSearchParams({ scope });
  return fetch(`${API_URL}/admin/pump-usage-analytics?${params}`, { headers: authHeaders() }).then(parseJson);
}

function businessInsightsQuery(fromDate, toDate, extra = {}) {
  const params = new URLSearchParams();
  if (fromDate) params.set('from_date', fromDate);
  if (toDate) params.set('to_date', toDate);
  Object.entries(extra).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  });
  const qs = params.toString() ? `?${params}` : '';
  return qs;
}

export function fetchAdminBusinessInsightsDateDefaults(pumpId) {
  return fetch(`${API_URL}/admin/pump/${pumpId}/business-insights/date-defaults`, { headers: authHeaders() }).then(parseJson);
}

export function fetchAdminBusinessInsightsModule(pumpId, moduleKey, fromDate = '', toDate = '', extra = {}) {
  const qs = businessInsightsQuery(fromDate, toDate, extra);
  return fetch(`${API_URL}/admin/pump/${pumpId}/business-insights/${moduleKey}${qs}`, { headers: authHeaders() }).then(parseJson);
}

export function fetchAdminAuditLogs(page = 1, pumpId = null, search = '', category = 'all') {
  const params = new URLSearchParams({ page: String(page), category });
  if (pumpId) params.set('pump_id', pumpId);
  if (search) params.set('search', search);
  return fetch(`${API_URL}/admin/audit-logs?${params}`, { headers: authHeaders() }).then(parseJson);
}

export function fetchNotificationHistory(page = 1, pumpId = null) {
  const params = new URLSearchParams({ page: String(page) });
  if (pumpId) params.set('pump_id', pumpId);
  return fetch(`${API_URL}/admin/notifications/history?${params}`, { headers: authHeaders() }).then(parseJson);
}

export function adminNotifyPump(pumpId, message, channels = ['email'], alertType = null) {
  return fetch(`${API_URL}/admin/alerts/notify`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ pump_id: pumpId, message, channels, alert_type: alertType }),
  }).then(parseJson);
}

export function fetchPumpInvoiceSettings(pumpId) {
  return fetch(`${API_URL}/admin/pump/${pumpId}/invoice-settings`, { headers: authHeaders() }).then(parseJson);
}

export function savePumpInvoiceSettings(pumpId, data) {
  return fetch(`${API_URL}/admin/pump/${pumpId}/invoice-settings`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(data),
  }).then(parseJson);
}

export function fetchGstStates() {
  return fetch(`${API_URL}/admin/gst-states`, { headers: authHeaders() }).then(parseJson);
}

export function fetchPumpInvoices(pumpId, filter) {
  const qs = filter ? `?filter=${encodeURIComponent(filter)}` : '';
  return fetch(`${API_URL}/admin/pump/${pumpId}/invoices${qs}`, { headers: authHeaders() }).then(parseJson);
}

export function fetchPumpInvoiceCustomers(pumpId) {
  return fetch(`${API_URL}/admin/pump/${pumpId}/invoice-customers`, { headers: authHeaders() }).then(parseJson);
}

export function createPumpDraftInvoice(pumpId, data) {
  return fetch(`${API_URL}/admin/pump/${pumpId}/invoices/draft`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(data),
  }).then(parseJson);
}

export function updatePumpDraftInvoice(pumpId, invoiceId, data) {
  return fetch(`${API_URL}/admin/pump/${pumpId}/invoices/${invoiceId}`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(data),
  }).then(parseJson);
}

export function approvePumpDraftInvoice(pumpId, invoiceId) {
  return fetch(`${API_URL}/admin/pump/${pumpId}/invoices/${invoiceId}/approve`, {
    method: 'POST',
    headers: authHeaders(),
  }).then(parseJson);
}

export function recordPumpInvoicePayment(pumpId, invoiceId, data) {
  return fetch(`${API_URL}/admin/pump/${pumpId}/invoices/${invoiceId}/payment`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(data),
  }).then(parseJson);
}

export function fetchClientAccountSummary(pumpId) {
  return fetch(`${API_URL}/admin/pump/${pumpId}/client-account`, { headers: authHeaders() }).then(parseJson);
}

export function fetchInvoiceReceipts(pumpId, invoiceId) {
  return fetch(`${API_URL}/admin/pump/${pumpId}/invoices/${invoiceId}/receipts`, { headers: authHeaders() }).then(parseJson);
}

export function addInvoiceReceipt(pumpId, invoiceId, data) {
  return fetch(`${API_URL}/admin/pump/${pumpId}/invoices/${invoiceId}/receipts`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(data),
  }).then(parseJson);
}

export function deleteInvoiceReceipt(pumpId, invoiceId, receiptId) {
  return fetch(`${API_URL}/admin/pump/${pumpId}/invoices/${invoiceId}/receipts/${receiptId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  }).then(parseJson);
}

export function fetchBillingConfig() {
  return fetch(`${API_URL}/admin/billing-config`, { headers: authHeaders() }).then(parseJson);
}

export function fetchMasterSettings() {
  return fetch(`${API_URL}/admin/master-settings`, { headers: authHeaders() }).then(parseJson);
}

export function saveMasterSettings(data) {
  return fetch(`${API_URL}/admin/master-settings`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(data),
  }).then(parseJson);
}

export function savePumpMaintenance(pumpId, data) {
  return fetch(`${API_URL}/admin/pump/${pumpId}/maintenance`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(data),
  }).then(parseJson);
}

export function fetchMasterPlans() {
  return fetch(`${API_URL}/admin/master-settings/plans`, { headers: authHeaders() }).then(parseJson);
}

export function saveMasterPlan(data, planId = null) {
  const url = planId
    ? `${API_URL}/admin/master-settings/plans/${planId}`
    : `${API_URL}/admin/master-settings/plans`;
  return fetch(url, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(data),
  }).then(parseJson);
}

export function deleteMasterPlan(planId) {
  return fetch(`${API_URL}/admin/master-settings/plans/${planId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  }).then(parseJson);
}

export function fetchMasterCoupons() {
  return fetch(`${API_URL}/admin/master-settings/coupons`, { headers: authHeaders() }).then(parseJson);
}

export function saveMasterCoupon(data, couponId = null) {
  const url = couponId
    ? `${API_URL}/admin/master-settings/coupons/${couponId}`
    : `${API_URL}/admin/master-settings/coupons`;
  return fetch(url, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(data),
  }).then(parseJson);
}

export function deleteMasterCoupon(couponId) {
  return fetch(`${API_URL}/admin/master-settings/coupons/${couponId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  }).then(parseJson);
}

export function fetchMasterAdmins() {
  return fetch(`${API_URL}/admin/master-settings/admins`, { headers: authHeaders() }).then(parseJson);
}

export function updateMasterAdmin(userId, data) {
  return fetch(`${API_URL}/admin/master-settings/admins/${userId}`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(data),
  }).then(parseJson);
}

export function fetchMessageChannels() {
  return fetch(`${API_URL}/admin/master-settings/message-channels`, { headers: authHeaders() }).then(parseJson);
}

function saveMessageRecord(path, data, id) {
  const url = id ? `${API_URL}/admin/master-settings/${path}/${id}` : `${API_URL}/admin/master-settings/${path}`;
  return fetch(url, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(data),
  }).then(parseJson);
}

function deleteMessageRecord(path, id) {
  return fetch(`${API_URL}/admin/master-settings/${path}/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  }).then(parseJson);
}

export function saveMailAccount(data, id = null) {
  return saveMessageRecord('mail-accounts', data, id);
}

export function deleteMailAccount(id) {
  return deleteMessageRecord('mail-accounts', id);
}

export function saveSmsAccount(data, id = null) {
  return saveMessageRecord('sms-accounts', data, id);
}

export function deleteSmsAccount(id) {
  return deleteMessageRecord('sms-accounts', id);
}

export function saveSmsTemplate(data, id = null) {
  return saveMessageRecord('sms-templates', data, id);
}

export function deleteSmsTemplate(id) {
  return deleteMessageRecord('sms-templates', id);
}

export function downloadPumpAnalyticsPdf(pumpId, fromDate, toDate) {
  const params = new URLSearchParams();
  if (fromDate) params.set('from_date', fromDate);
  if (toDate) params.set('to_date', toDate);
  const qs = params.toString() ? `?${params}` : '';
  return fetch(`${API_URL}/admin/pump/${pumpId}/analytics/pdf${qs}`, {
    headers: {
      Authorization: 'Bearer ' + localStorage.getItem('AUTH_TOKEN'),
      Accept: 'application/pdf',
    },
  })
    .then((res) => {
      if (!res.ok) throw new Error('PDF export failed');
      return res.blob();
    })
    .then((blob) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pump-analytics-${pumpId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    });
}

export function fetchPumpAccountingScan(pumpId, fromDate = '', toDate = '') {
  const params = new URLSearchParams();
  if (fromDate) params.set('from_date', fromDate);
  if (toDate) params.set('to_date', toDate);
  const qs = params.toString() ? `?${params}` : '';
  return fetch(`${API_URL}/admin/pump/${pumpId}/accounting/scan${qs}`, { headers: authHeaders() }).then(parseJson);
}

export function fetchPumpAccountingFixes(pumpId) {
  return fetch(`${API_URL}/admin/pump/${pumpId}/accounting/fixes`, { headers: authHeaders() }).then(parseJson);
}

export function previewPumpAccountingFix(pumpId, fixType, params = {}) {
  return fetch(`${API_URL}/admin/pump/${pumpId}/accounting/preview`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ fix_type: fixType, params }),
  }).then(parseJson);
}

export function applyPumpAccountingFix(pumpId, fixType, params, reason, findingType = null) {
  return fetch(`${API_URL}/admin/pump/${pumpId}/accounting/apply`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ fix_type: fixType, params, reason, finding_type: findingType }),
  }).then(parseJson);
}

export function fetchPumpAccountingCases(pumpId, page = 1, fixType = '') {
  const params = new URLSearchParams({ page: String(page) });
  if (fixType) params.set('fix_type', fixType);
  return fetch(`${API_URL}/admin/pump/${pumpId}/accounting/cases?${params}`, { headers: authHeaders() }).then(parseJson);
}

export function fetchPumpAccountingStats(pumpId) {
  return fetch(`${API_URL}/admin/pump/${pumpId}/accounting/stats`, { headers: authHeaders() }).then(parseJson);
}

export function fetchPumpAccountingWacAudits(pumpId, limit = 10) {
  return fetch(`${API_URL}/admin/pump/${pumpId}/accounting/wac-audits?limit=${limit}`, { headers: authHeaders() }).then(parseJson);
}

export function fetchPumpAccountingWacProgress(pumpId) {
  return fetch(`${API_URL}/admin/pump/${pumpId}/accounting/wac-progress`, { headers: authHeaders() }).then(parseJson);
}

export function releasePumpAccountingApplyLock(pumpId) {
  return fetch(`${API_URL}/admin/pump/${pumpId}/accounting/release-apply-lock`, {
    method: 'POST',
    headers: authHeaders(),
  }).then(parseJson);
}

export function fetchPumpOwnerFundAnalytics(pumpId, fromDate = '', toDate = '') {
  const params = new URLSearchParams();
  if (fromDate) params.set('from_date', fromDate);
  if (toDate) params.set('to_date', toDate);
  const qs = params.toString() ? `?${params}` : '';
  return fetch(`${API_URL}/admin/pump/${pumpId}/owner-fund-analytics${qs}`, { headers: authHeaders() }).then(parseJson);
}

export function fetchPumpOwnerFundDifferenceDetail(pumpId, acDate, fromDate = '', toDate = '') {
  const params = new URLSearchParams({ ac_date: acDate });
  if (fromDate) params.set('from_date', fromDate);
  if (toDate) params.set('to_date', toDate);
  return fetch(`${API_URL}/admin/pump/${pumpId}/owner-fund-analytics/difference?${params}`, {
    headers: authHeaders(),
  }).then(parseJson);
}

export function fetchPumpAccountingCase(pumpId, caseId) {
  return fetch(`${API_URL}/admin/pump/${pumpId}/accounting/cases/${caseId}`, { headers: authHeaders() }).then(parseJson);
}

export function revertPumpAccountingCase(pumpId, caseId, reason) {
  return fetch(`${API_URL}/admin/pump/${pumpId}/accounting/cases/${caseId}/revert`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ reason }),
  }).then(parseJson);
}

export function fetchSystemMonitor() {
  return fetch(`${API_URL}/admin/system-monitor`, { headers: authHeaders() }).then(parseJson);
}

export function fetchSystemMonitorAlerts() {
  return fetch(`${API_URL}/admin/system-monitor/alerts`, { headers: authHeaders() }).then(parseJson);
}

export function saveSystemMonitorAlerts(payload) {
  return fetch(`${API_URL}/admin/system-monitor/alerts`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  }).then(parseJson);
}

export function testSystemMonitorAlert(payload = {}) {
  return fetch(`${API_URL}/admin/system-monitor/alerts/test`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  }).then(parseJson);
}

export function startSystemMonitorQueue(queueKey) {
  return fetch(`${API_URL}/admin/system-monitor/queue/start`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ queue_key: queueKey }),
  }).then(parseJson);
}

export function stopSystemMonitorQueue(queueKey) {
  return fetch(`${API_URL}/admin/system-monitor/queue/stop`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ queue_key: queueKey }),
  }).then(parseJson);
}

export function restartSystemMonitorQueue(queueKey) {
  return fetch(`${API_URL}/admin/system-monitor/queue/restart`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ queue_key: queueKey }),
  }).then(parseJson);
}

export function pruneSystemMonitorReports({ days, dryRun } = {}) {
  return fetch(`${API_URL}/admin/system-monitor/reports/prune`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      days: days || undefined,
      dry_run: !!dryRun,
    }),
  }).then(parseJson);
}

export function processSystemMonitorReportPdfs() {
  return fetch(`${API_URL}/admin/system-monitor/report-pdfs/process`, {
    method: 'POST',
    headers: authHeaders(),
  }).then(parseJson);
}

export function retrySystemMonitorReportPdf(id) {
  return fetch(`${API_URL}/admin/system-monitor/report-pdfs/${id}/retry`, {
    method: 'POST',
    headers: authHeaders(),
  }).then(parseJson);
}

export function failSystemMonitorReportPdf(id, reason) {
  return fetch(`${API_URL}/admin/system-monitor/report-pdfs/${id}/fail`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ reason: reason || '' }),
  }).then(parseJson);
}

export function systemMonitorDiagnosticsUrl(path, extra = '') {
  const qs = extra ? `?${extra}` : '';
  return `${API_URL}/admin/system-monitor/diagnostics/${path}${qs}`;
}

export function systemMonitorAuthHeaders() {
  return {
    Authorization: 'Bearer ' + localStorage.getItem('AUTH_TOKEN'),
    Accept: 'application/json',
  };
}
